import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Button } from '@mui/material';

import { Page, useAppLayoutContextConsumer, snackbar } from '@occc-app/design-system';
import { LinkItem, LoadingCard } from '@occc/ui-design-system';
import { useUpdateKioskAdSetMutation, Kiosk_Ad_Set_Status } from '@occc/graphql';

import { AdSetsEditPage } from './ad-sets-edit-page.component';

import { SyncInfoDialog } from '../../components/sync-info-dialog.component';
import { AdSetFormValues } from '../../touchpoint.types';
import { routeCreator } from '../../touchpoint.router';
import { useGetKioskAdSet } from '../../hooks/use-get-ad-set.hook';
import {
  AD_SET_UPDATE_SUCCESS_MESSAGE,
  AD_SET_UPDATE_ERROR_MESSAGE,
  AD_SET_SYNC_INFO_MESSAGE,
} from '../../touchpoint.constants';
import { isScheduleValid, mapScheduleToScheduleInput } from '../../utils/ad-sets-form.validation';
const PAGE_TITLE = 'Edit Ad Set';

export const AdSetsEditPageContainer = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSyncInfoDialog, setShowSyncInfoDialog] = useState(false);

  const { loading, error, adSet } = useGetKioskAdSet({ id: id || '' });
  const [updateKioskAdSet] = useUpdateKioskAdSetMutation();

  const initialValues: Partial<AdSetFormValues> = useMemo(() => {
    const kioskIds = (adSet?.kiosks.map((kiosk) => kiosk?.id).filter(Boolean) as string[]) || [];
    const emptySchedule = { days: [], startTime: '', endTime: '' };

    if (!adSet) {
      return {
        name: '',
        description: '',
        assetId: null,
        offer: null,
        kioskIds: [],
        schedules: [emptySchedule],
      };
    }

    const schedules = adSet.schedules?.length
      ? adSet.schedules.map(mapScheduleToScheduleInput)
      : [emptySchedule];

    return {
      name: adSet.name || '',
      description: adSet.description || '',
      assetId: adSet.asset?.id || null,
      offer: null,
      kioskIds,
      schedules,
    };
  }, [adSet]);

  const initialOfferId = adSet?.offerId ?? undefined;

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.adSetsList(),
        label: 'Touchpoint',
      },
      {
        key: routeCreator.adSetsList(),
        label: 'Ad Sets',
      },
    ],
    [],
  );

  useAppLayoutContextConsumer(breadcrumbLinks);

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSyncInfoDialogClose = useCallback(() => {
    setShowSyncInfoDialog(false);
    navigate(routeCreator.adSetsList());
  }, [navigate]);

  const onSubmit = useCallback(
    async (values: AdSetFormValues) => {
      if (!id) return;

      setIsSubmitting(true);

      const schedules = (values.schedules ?? [])
        .filter(isScheduleValid)
        .map(mapScheduleToScheduleInput);

      try {
        const { data } = await updateKioskAdSet({
          variables: {
            input: {
              id,
              name: values.name.trim(),
              description: values.description.trim(),
              assetId: values.assetId,
              offerId: values.offer?.id ?? null,
              kioskIds: values.kioskIds || [],
              schedules,
            },
          },
          refetchQueries: ['kioskAdSets'],
        });

        if (data?.updateKioskAdSet) {
          snackbar.success(AD_SET_UPDATE_SUCCESS_MESSAGE);
          if (data?.updateKioskAdSet?.status === Kiosk_Ad_Set_Status.Published) {
            setShowSyncInfoDialog(true);
          } else {
            navigate(routeCreator.adSetsList());
          }
        } else {
          snackbar.error(AD_SET_UPDATE_ERROR_MESSAGE);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error updating ad set:', error);
        snackbar.error(AD_SET_UPDATE_ERROR_MESSAGE);
        setIsSubmitting(false);
      }
    },
    [updateKioskAdSet, navigate, id],
  );

  if (loading && !adSet) {
    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: 'Loading...',
        }}
      >
        <LoadingCard />
      </Page>
    );
  }

  if (error || !adSet) {
    return (
      <Page
        browserTabTitle={PAGE_TITLE}
        pageHeaderProps={{
          title: PAGE_TITLE,
          subtitle: 'Error',
        }}
      >
        <Card>
          <CardContent>
            <Typography color='error'>{error?.message || 'Ad Set not found'}</Typography>
            <Button onClick={handleCancel} sx={{ mt: 2 }}>
              Back
            </Button>
          </CardContent>
        </Card>
      </Page>
    );
  }

  return (
    <>
      <AdSetsEditPage
        breadcrumbLinks={breadcrumbLinks}
        initialValues={initialValues}
        initialOfferId={initialOfferId}
        onSubmit={onSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
      <SyncInfoDialog
        open={showSyncInfoDialog}
        onClose={handleSyncInfoDialogClose}
        message={AD_SET_SYNC_INFO_MESSAGE}
      />
    </>
  );
};
